import { ReactNode, SyntheticEvent } from 'react';
import { SxProps, TabProps, TabsProps, Theme } from '@mui/material';

import { a11yProps, TabPanel } from 'components/TabPanel';
import { useQueryParams } from 'hooks/useQueryParams';
import { getWduiTabWidthSx } from 'theme/getWduiTabWidthSx';

export type TabData = {
  tabId: string;
  label: string | JSX.Element;
  adornment?: ReactNode;
  enabled: boolean;
  isPending?: boolean;
  content: ReactNode;
  panelSx?: SxProps<Theme>;
};

type UseTabs = {
  tabsProps: TabsProps;
  tabProps: TabProps[];
  tabPanels: JSX.Element[];
};

export const useTabs = (props: {
  idPrefix: string;
  ariaLabel: string;
  tabData: TabData[];
  onChangeTab?: (prevTabId: string | null, newTabId: string) => void;
  canChangeTab?: (
    prevTabId: string | null,
    newTabId: string,
  ) => boolean | Promise<boolean>;
  keepMounted?: boolean;
  queryParam?: string;
}): UseTabs => {
  const {
    idPrefix,
    ariaLabel,
    tabData,
    onChangeTab,
    canChangeTab,
    keepMounted,
    queryParam = 'tab',
  } = props;
  const defaultTab = tabData.find((child) => child.enabled)?.tabId || '';
  const [queryParams, setQueryParams] =
    useQueryParams<Record<string, string | null>>();
  const activeTabId = queryParams[queryParam] ?? defaultTab;

  const setActiveTab = (value: string): void => {
    // Preserve unrelated params (e.g. `?search=`) when switching tabs. A tab
    // that needs to reset other state should clear it explicitly.
    setQueryParams({
      ...queryParams,
      [queryParam]: value === defaultTab ? null : value,
    });
  };

  const isActiveTabPending = tabData.some(
    (child) => child.tabId === activeTabId && child.isPending,
  );

  // handle case where navigation to invalid or disabled tab
  if (
    defaultTab &&
    !isActiveTabPending &&
    !tabData.some((child) => child.tabId === activeTabId && child.enabled)
  ) {
    setActiveTab(defaultTab);
  }

  // Show the default tab while the requested one is pending, keeping it in the URL
  const displayedTabId = isActiveTabPending ? defaultTab : activeTabId;

  const tabsProps = {
    variant: 'fullWidth' as const,
    'aria-label': ariaLabel,
    indicatorColor: 'primary' as const,
    value: displayedTabId,
    onChange: async (_: SyntheticEvent, newValue: string) => {
      if (canChangeTab && !(await canChangeTab(displayedTabId, newValue))) {
        return;
      }
      setActiveTab(newValue);
      onChangeTab?.(displayedTabId, newValue);
    },
    TabIndicatorProps: { children: <span /> },
  };

  const enabledTabs = tabData.filter((child) => child.enabled);
  const tabWidthSx = getWduiTabWidthSx(enabledTabs.length);

  const tabProps = enabledTabs.map((child, index) => {
    return {
      key: child.tabId,
      value: child.tabId,
      sx: tabWidthSx,
      label: (
        <>
          <span>{child.label}</span>
          {child.adornment}
        </>
      ),
      ...a11yProps(idPrefix, index),
    };
  });

  const tabPanels = enabledTabs.map((child) => (
    <TabPanel
      key={child.tabId}
      index={child.tabId}
      value={displayedTabId || ''}
      idPrefix={idPrefix}
      keepMounted={keepMounted}
      sx={child.panelSx}
    >
      {child.content}
    </TabPanel>
  ));

  return { tabsProps, tabProps, tabPanels };
};
